export const AMPLITUDE_GROUP_TYPE = 'Group';
export const AMPLITUDE_MAX_GROUPS = 10;

/**
 * Role names ordered from highest to lowest privilege. Used to pick a single
 * representative role for an event so Amplitude tours can target one role
 * without enumerating the full `role_names` array. Mirrors the LMS role display
 * names (see app/Models/Role.php).
 */
export const ROLE_PRIORITY = [
    'Owner',
    'Admin',
    'Instruction Admin',
    'Instructor',
    'Instructional Assistant',
    'Teaching Assistant',
    'Content Editor',
    'Technical Writer',
    'Page Editor',
    'Remediation Manager',
    'Attempt Authorizer',
    'Attendance Manager',
    'Offsite Coordinator',
    'Proctor',
    'Student Support',
    'Student',
    'Passive',
];

/**
 * Pick the highest-privilege role from a list of role names.
 *
 * @param {string[]} roleNames - Role names (e.g. from buildAmplitudeUserTraits).
 * @returns {string|null} The highest-privilege role, or the first available
 *   role if none are in the priority list, or null when there are no roles.
 */
export const getPrimaryRoleName = roleNames => {
    if (!Array.isArray(roleNames) || roleNames.length === 0) {
        return null;
    }

    const prioritized = ROLE_PRIORITY.find(role => roleNames.includes(role));

    return prioritized ?? roleNames[0];
};

const uniqueSortedNames = names => {
    return [
        ...new Set(
            names
                .filter(name => typeof name === 'string')
                .map(name => name.trim())
                .filter(name => name !== '')
        ),
    ].sort((a, b) => a.localeCompare(b));
};

const getUserGroups = user => {
    if (!user) {
        return [];
    }

    return user.user_groups ?? user.userGroups ?? [];
};

/**
 * @param {string[]} groupNames
 * @param {number} [limit]
 * @returns {{ groupNames: string[], truncated: boolean }}
 */
export const capGroupNamesForAmplitude = (groupNames, limit = AMPLITUDE_MAX_GROUPS) => {
    if (groupNames.length <= limit) {
        return { groupNames, truncated: false };
    }

    return {
        groupNames: groupNames.slice(0, limit),
        truncated: true,
    };
};

/**
 * @param {Object|null|undefined} user
 * @returns {{ roleNames: string[], groupNames: string[], amplitudeGroupNames: string[], groupsTruncated: boolean }}
 */
export const buildAmplitudeUserTraits = user => {
    if (!user) {
        return {
            roleNames: [],
            groupNames: [],
            amplitudeGroupNames: [],
            groupsTruncated: false,
        };
    }

    const directRoleNames = (user.roles ?? []).map(role => role?.name);
    const groupRoleNames = getUserGroups(user).map(userGroup => userGroup?.role?.name);
    const roleNames = uniqueSortedNames([...directRoleNames, ...groupRoleNames]);

    const groupNames = uniqueSortedNames(getUserGroups(user).map(userGroup => userGroup?.group?.name));
    const { groupNames: amplitudeGroupNames, truncated: groupsTruncated } = capGroupNamesForAmplitude(groupNames);

    return {
        roleNames,
        groupNames,
        amplitudeGroupNames,
        groupsTruncated,
    };
};
